import type { ConnectorView } from '@tabel/shared/schemas/agent-client';
import { useQuery } from '@tanstack/react-query';
import { Laptop, Unplug } from 'lucide-react';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { ErrorCodes, errorCode, errorText, useToast } from '../../components/ErrorToast';
import { AlertDialog, Badge, Button, Card, CardHeader, EmptyState, Skeleton } from '../../components/ui';
import { adminConnectors, disableConnector, isGone } from '../../lib/agent-api';
import { ApiError, reportClientError } from '../../lib/errors';
import { useRole } from '../../lib/role';
import { OS_LABEL, PC_LABEL, PC_T, pcSay } from '../MyAgents/texts';
import { sinceText } from '../MyAgents/time';
import { Fact, LoadError, SCREEN, shouldReport } from './parts';
import { LABEL, T, TITLE, say } from './texts';

/*
 * Компьютеры всех пользователей — вкладка «Компьютеры» страницы «Данные для ИИ-агентов» (006, T116; US7; spec.md,
 * «Права ролей», последняя строка; contracts/http-api.md: `GET /api/admin/agent-connectors`,
 * `POST /api/agent/connectors/:id/disable`; contracts/ui.md §1). Только администратор системы: другим ролям пункта меню
 * нет, сервер отвечает `403`.
 * - Строка: имя компьютера, бейдж состояния («в сети», «не в сети с 14:05» — по времени объекта, «отключён»),
 *   владелец (вход в табель), система, версия коннектора. Переписки нет: ни разговоров, ни их числа в строке.
 * - «Отключить» — красная текстовая кнопка (дизайн-система, Button). Отключение необратимо и касается другого человека —
 *   окно подтверждения: вопрос с именем компьютера, последствия числами (разговоры перестанут работать, переписка
 *   останется), чей компьютер; «Отмена» в фокусе. Итог — список заново, сообщение и фокус на заголовке карточки;
 *   `404` — компьютера уже нет: список заново и сообщение. Ошибка — в окне, с кодом ошибки.
 * - Состояния: загрузка — скелет строк, пусто — «Компьютеров нет», ошибка — причина, код ошибки и «Повторить».
 * - Один интерфейс на 375/768/1280 px: сведения строки — в три колонки, на телефоне — в две; кнопка на телефоне —
 *   внизу строки, во всю ширину, 40 px.
 * Тексты общие с «Мои ИИ-клиенты» (компьютеры и отключение) — `MyAgents/texts.ts`; свои — `texts.ts` страницы.
 */

export const adminConnectorsKey = (role: string) => ['agent-connectors-admin', role] as const;

export function ComputersTab({ tz }: { tz: string }) {
  const { role } = useRole();
  const q = useQuery({ queryKey: adminConnectorsKey(role), queryFn: adminConnectors });
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [focusTick, setFocusTick] = useState(0);
  const [target, setTarget] = useState<ConnectorView | null>(null);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const { showInfo } = useToast();

  useEffect(() => {
    if (q.error && shouldReport(q.error)) reportClientError(SCREEN, 'load-computers', q.error);
  }, [q.error]);
  useEffect(() => {
    if (focusTick > 0) headingRef.current?.focus();
  }, [focusTick]);

  /** Отключение закончено: окно закрыто, сообщение, фокус — на заголовке (эффект идёт после эффекта окна). */
  const finish = (message: string) => {
    setTarget(null);
    showInfo(message);
    setFocusTick((t) => t + 1);
  };

  const confirm = async (pc: ConnectorView) => {
    setPending(true);
    setFailure(null);
    try {
      await disableConnector(pc.id);
      await q.refetch();
      finish(pcSay.disabled(pc.name));
    } catch (e) {
      if (isGone(e)) {
        await q.refetch();
        finish(pcSay.alreadyDisabled(pc.name));
      } else {
        setFailure(e);
        if (shouldReport(e)) reportClientError(SCREEN, 'disable-connector', e);
      }
    } finally {
      setPending(false);
    }
  };

  let body: ReactNode;
  if (q.isError && !q.data) {
    return (
      <LoadError
        title={TITLE.computersError}
        error={q.error}
        busy={q.isFetching}
        onRetry={() => void q.refetch()}
      />
    );
  }
  if (!q.data) body = <Loading />;
  else if (q.data.length === 0)
    body = (
      <div className="border-t border-line">
        <EmptyState icon={Laptop} title={TITLE.computersEmpty} description={T.computersEmpty} />
      </div>
    );
  else
    body = (
      <ul aria-label={TITLE.computers} className="divide-y divide-line border-t border-line">
        {q.data.map((pc) => (
          <ComputerRow
            key={pc.id}
            pc={pc}
            tz={tz}
            onDisable={() => {
              setFailure(null);
              setTarget(pc);
            }}
          />
        ))}
      </ul>
    );

  return (
    <section aria-labelledby={headingId}>
      <Card>
        <CardHeader
          id={headingId}
          headingRef={headingRef}
          title={TITLE.computers}
          description={T.computersIntro}
        />
        {body}
      </Card>
      <AlertDialog
        open={target !== null}
        // Пока идёт отключение, окно не закрывается: итог покажет сообщение.
        onClose={() => {
          if (!pending) setTarget(null);
        }}
        title={target ? pcSay.disableTitle(target.name) : ''}
        icon={Unplug}
        tone="bad"
        description={
          <>
            {target ? pcSay.disableText(target.threads) : ''}
            {target?.owner && ` ${say.computerOwner(target.owner)}`}
            {failure !== null && <DisableError error={failure} />}
          </>
        }
      >
        <Button data-autofocus disabled={pending} onClick={() => setTarget(null)}>
          {PC_LABEL.cancel}
        </Button>
        <Button variant="danger" disabled={pending} onClick={() => target && void confirm(target)}>
          {pending ? PC_LABEL.disabling : PC_LABEL.disableConfirm}
        </Button>
      </AlertDialog>
    </section>
  );
}

function ComputerRow({ pc, tz, onDisable }: { pc: ConnectorView; tz: string; onDisable: () => void }) {
  const nameId = useId();
  const disabled = pc.phase === 'disabled';
  const os = OS_LABEL[pc.os as keyof typeof OS_LABEL] ?? pc.os;
  const badge = disabled ? (
    <Badge dot>{PC_LABEL.disabled}</Badge>
  ) : pc.online ? (
    <Badge tone="ok" dot>
      {PC_LABEL.online}
    </Badge>
  ) : (
    <Badge tone="warn" dot>
      {pcSay.offlineSince(sinceText(pc.lastSeenAt, tz))}
    </Badge>
  );
  return (
    <li
      aria-labelledby={nameId}
      className="flex flex-wrap items-center gap-x-4 gap-y-2 px-6 py-3 max-sm:px-4"
    >
      <div className="flex min-w-0 flex-[1_1_220px] items-center gap-2.5">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-subtle text-ink-2">
          <Laptop size={18} strokeWidth={1.75} aria-hidden />
        </span>
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <h3 id={nameId} className="min-w-0 text-[14px] font-semibold break-all text-ink">
            {pc.name}
          </h3>
          <span data-badge className="shrink-0">
            {badge}
          </span>
        </div>
      </div>
      {/* На телефоне строка — карточка: действие внизу, во всю ширину. */}
      {!disabled && (
        <Button
          variant="dangerText"
          aria-label={pcSay.disableName(pc.name)}
          onClick={onDisable}
          className="max-sm:order-last max-sm:w-full"
        >
          {PC_LABEL.disable}
        </Button>
      )}
      <dl className="grid w-full grid-cols-3 gap-x-6 gap-y-2 max-sm:grid-cols-2">
        <Fact label={LABEL.owner}>
          <span className="break-all">{pc.owner ?? '—'}</span>
        </Fact>
        <Fact label={LABEL.system}>{os}</Fact>
        <Fact label={LABEL.connector}>
          <span className="tabular-nums">{pc.version ?? '—'}</span>
        </Fact>
      </dl>
    </li>
  );
}

function Loading() {
  return (
    <div
      aria-busy="true"
      aria-label={LABEL.computersLoading}
      className="divide-y divide-line border-t border-line"
    >
      {[0, 1].map((i) => (
        <div key={i} className="flex items-center gap-3 px-6 py-3 max-sm:px-4">
          <Skeleton className="h-9 w-9 shrink-0" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-5 w-16" />
        </div>
      ))}
    </div>
  );
}

/** Ошибка отключения в окне: окно остаётся, текст и код ошибки (requestId) для команды разработки. */
function DisableError({ error }: { error: unknown }) {
  const requestId = error instanceof ApiError ? error.requestId : null;
  const code = errorCode(error);
  return (
    <span role="alert" className="mt-3 block rounded-control bg-bad-soft px-3 py-2 text-[13px] text-bad">
      <span className="block font-semibold">{PC_T.disableFailed}.</span>
      <span className="block">{errorText(error)}</span>
      {(requestId || code) && (
        <span className="mt-0.5 block text-[12px]">
          {LABEL.errorCode} <ErrorCodes requestId={requestId} code={code} />
        </span>
      )}
    </span>
  );
}
